import axios from 'axios';
import Helper from './utils/Helper';
import Smartlook from 'smartlook-client'
import { HelmetProvider } from 'react-helmet-async';
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from './components/Error/Error';
import { Helmet } from 'react-helmet';
import './App.css';
import { WebSiteRoutes } from './WebSiteRoutes/WebSiteRoutes';
import { NavBar } from "./commonComponents";
import { Footer } from './commonComponents';
import { MobileBottomNavBar } from './commonComponents';

import * as amplitude from '@amplitude/analytics-browser';

// ShareChat Analytics import
import ShareChatAnalytics from './utils/ShareChatAnalytics';

amplitude.init('133a07dbe49858cf11e64e98f1ac1fc9', {
  defaultTracking: {
    pageViews: true,
    sessions: true,
    formInteractions: true,
    fileDownloads: true,
  },

});


if (process.env.REACT_APP_ENV === 'production') {
  Smartlook.init('16ede8dcfbea40c36dd752c9a5dec79f5674d402')
}

function App() {
  localStorage.setItem('webApp', true);
  const [shareChatInitialized, setShareChatInitialized] = useState(false);

  const location = useLocation();
  let { pathname } = location;
  pathname = pathname.split('/')[1];

  // Initialize ShareChat SDK
  useEffect(() => {
    // Initialize ShareChat SDK if not already initialized
    if (!window.scq) {
      console.log("Initializing ShareChat SDK...");

      const initShareChat = () => {
        window.scLayer = window.scLayer || [];
        window.scSdkId = "6xQnb3_KTK";

        window.scq = function (eventName, eventType, p) {
          const props = p || {};
          window.scLayer.push({
            eventName: eventName,
            eventType: eventType,
            meta: props,
            eventFireTs: Date.now()
          });
        };

        // Create and load the script
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = 'https://sc-events-sdk.sharechat.com/web-sdk.js';
        script.onload = () => {
          console.log("ShareChat SDK loaded successfully");
          setShareChatInitialized(true);
        };
        script.onerror = () => {
          console.error("Failed to load ShareChat SDK");
        };

        // Append the script to the head
        document.head.appendChild(script);
      };

      initShareChat();
    } else {
      setShareChatInitialized(true);
      console.log("ShareChat SDK already initialized");
    }
  }, []);

  // Track page view on route change with ShareChat
  useEffect(() => {
    const trackPageView = () => {
      if (window.scq) {
        try {
          window.scq("PAGE_VIEW", "AUTO", {
            pageUrl: window.location.href,
            pagePath: location.pathname
          });
          console.log("ShareChat: Tracked page view for", location.pathname);
        } catch (error) {
          console.error("Error tracking ShareChat page view:", error);
        }
      } else {
        console.warn("ShareChat SDK not ready for page view tracking, will retry...");
        // Retry after a short delay
        setTimeout(trackPageView, 1000);
      }
    };

    // Wait a moment to ensure SDK is loaded
    if (shareChatInitialized) {
      trackPageView();
    } else {
      // Check periodically if SDK is ready
      const checkInterval = setInterval(() => {
        if (window.scq) {
          clearInterval(checkInterval);
          trackPageView();
        }
      }, 500);

      // Clean up interval if component unmounts
      return () => clearInterval(checkInterval);
    }
  }, [location, shareChatInitialized]);

  let showNavBar = !pathname ? false : ['/login', '/verify-user', '/signup', '/login/verify-user'].toString().includes(pathname);
  const showBottomNavBar = ['/', '/marketplace/', '/fleektab/', '/account/'].toString().includes(pathname)
  const isMobile = Helper.isMobile()

  axios.defaults.baseURL = process.env.REACT_APP_API_URL;
  const isLoggedIn = localStorage.getItem('accesstoken') ? true : false;
  if (isLoggedIn) {
    axios.defaults.headers.common.Authorization = localStorage.getItem('accesstoken');
  }

  axios.interceptors.response.use((response) => response, (error) => {
    if (error && error.response && error.response.status === 401) {
      localStorage.removeItem('accesstoken');
      window.location.href = '/login';
    }
    return error;
  });

  /**
   * Hash string using SHA-256
   * @param {string} str - String to hash
   * @returns {string} - Hashed string
   */
  const sha256 = async (str) => {
    // Use the Web Crypto API which is supported in all modern browsers
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    
    // Convert the hash to a hex string
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  };

  // Get Snapchat user identifiers for initialization
  const getSnapchatUserIdentifiers = async () => {
    const phoneNo = localStorage.getItem('phoneNo') || '';
    const uuid = localStorage.getItem('uuid_c1') || '';
    
    // Properly hash the phone number using SHA-256
    let hashedPhone = '';
    if (phoneNo) {
      try {
        hashedPhone = await sha256(phoneNo);
      } catch (error) {
        console.error("Error hashing phone number for Snapchat pixel:", error);
      }
    }
    
    // Only return parameters that exist
    const params = {};
    if (uuid) params.uuid_c1 = uuid;
    if (phoneNo) params.user_phone_number = phoneNo;
    if (hashedPhone) params.user_hashed_phone_number = hashedPhone;
    
    return params;
  };
  
  // Generate Snapchat init code with optional user data
  const generateSnapchatInitCode = () => {
    // Since we can't use async-await directly in the render function,
    // we need to initialize with a basic script and then update user data later
    const initScript = `
      (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function()
      {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
      a.queue=[];var s='script';r=t.createElement(s);r.async=!0;
      r.src=n;var u=t.getElementsByTagName(s)[0];
      u.parentNode.insertBefore(r,u);})(window,document,
      'https://sc-static.net/scevent.min.js');
      
      snaptr('init', 'ddeb6521-c4d7-4abf-965d-93f953d26782');
      snaptr('track', 'PAGE_VIEW');
      
      // We'll update with user data after initialization if it's available
      (async function() {
        try {
          // Hash function implementation directly in the script
          const sha256 = async (str) => {
            const encoder = new TextEncoder();
            const data = encoder.encode(str);
            const hashBuffer = await crypto.subtle.digest('SHA-256', data);
            return Array.from(new Uint8Array(hashBuffer))
              .map(b => b.toString(16).padStart(2, '0'))
              .join('');
          };
          
          // Get user data
          const phoneNo = localStorage.getItem('phoneNo') || '';
          const uuid = localStorage.getItem('uuid_c1') || '';
          
          // Only proceed if we have data to add
          if (!phoneNo && !uuid) return;
          
          // Prepare user data
          const userData = {};
          if (uuid) userData.uuid_c1 = uuid;
          if (phoneNo) {
            userData.user_phone_number = phoneNo;
            const hashedPhone = await sha256(phoneNo);
            if (hashedPhone) userData.user_hashed_phone_number = hashedPhone;
          }
          
          // Update tracking with user data
          if (Object.keys(userData).length > 0) {
            snaptr('track', 'PAGE_VIEW', userData);
          }
        } catch (e) {
          console.error('Error updating Snapchat with user data:', e);
        }
      })();
    `;
    
    return initScript;
  };

  return (
    <HelmetProvider >
      <div className="App">
        <Helmet>
          {/* Google Tags (gtag.js) */}
          <script async src="https://www.googletagmanager.com/gtag/js?id=AW-10792886696"></script>
          <script async src="https://www.googletagmanager.com/gtag/js?id=G-F48MNX3TS6"></script>
          <script>
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'AW-10792886696');
              gtag('config', 'G-F48MNX3TS6');
            `}
          </script>

          {/* Meta Pixel Code - Multiple pixels */}
          <script>
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              
              // Initialize all four pixel IDs
              fbq('init', '598569026163339');
              fbq('init', '1698931937358791');
              fbq('init', '625503420350787');
              fbq('init', '1374617340295052');
              
              // Track page view for all pixels
              fbq('track', 'PageView');
            `}
          </script>
          
          {/* Snapchat Pixel Code */}
          <script>
            {generateSnapchatInitCode()}
          </script>
          
          {/* Facebook Noscript Fallbacks */}
          <noscript>
            {`
              <!-- Facebook Pixel Fallbacks -->
              <img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=598569026163339&ev=PageView&noscript=1" />
              <img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=1698931937358791&ev=PageView&noscript=1" />
              <img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=625503420350787&ev=PageView&noscript=1" />
              <img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=1374617340295052&ev=PageView&noscript=1" />
              
              <!-- Snapchat Pixel Fallback -->
              <img height="1" width="1" style="display:none" src="https://tr.snapchat.com/p?pid=ddeb6521-c4d7-4abf-965d-93f953d26782&ev=PAGE_VIEW&noscript=1" />
            `}
          </noscript>
        </Helmet>

        {!isMobile && !showNavBar && <NavBar isLoggedIn={isLoggedIn} />}
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <WebSiteRoutes isLoggedIn={isLoggedIn} />
        </ErrorBoundary>
        {isMobile && showBottomNavBar &&
          <>
            <div className='spacer'>
              <MobileBottomNavBar type={pathname} isLoggedIn={isLoggedIn} />
            </div>
          </>
        }
        {!isMobile && !showNavBar && <Footer />}
      </div>
    </HelmetProvider>
  );
}

export default App;